import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Plus, PencilSimple, Trash, ArrowUp, ArrowDown, FloppyDisk, Sparkle, ShieldStar,
} from "@phosphor-icons/react";

export default function PipelinesManage() {
  const { currentOrg } = useAuth();
  const isAdmin = currentOrg?.role === "admin";
  const [pipelines, setPipelines] = useState([]);
  const [activeId, setActiveId] = useState("");
  const [loading, setLoading] = useState(true);

  // Draft state for the currently-open pipeline's stages
  const [draftStages, setDraftStages] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [savingStages, setSavingStages] = useState(false);

  // Rename / description edit
  const [renaming, setRenaming] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [renameDesc, setRenameDesc] = useState("");

  // Create pipeline dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newForm, setNewForm] = useState({ name: "", description: "", stages: "New" });
  const [creating, setCreating] = useState(false);

  // Delete pipeline
  const [confirmDelete, setConfirmDelete] = useState(null);

  const active = useMemo(
    () => pipelines.find((p) => p.pipeline_id === activeId) || null,
    [pipelines, activeId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pipelines");
      setPipelines(data);
      const next = data.find((p) => p.pipeline_id === activeId) ? activeId : (data[0]?.pipeline_id || "");
      setActiveId(next);
    } catch {
      toast.error("Could not load pipelines");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.org_id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!active) { setDraftStages([]); setDirty(false); return; }
    setDraftStages(active.stages.map((s) => ({ stage_id: s.stage_id, name: s.name })));
    setDirty(false);
    setRenameName(active.name);
    setRenameDesc(active.description || "");
    setRenaming(false);
  }, [active?.pipeline_id, active?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStage = (idx, name) => {
    setDraftStages((s) => s.map((x, i) => (i === idx ? { ...x, name } : x)));
    setDirty(true);
  };
  const moveStage = (idx, dir) => {
    setDraftStages((s) => {
      const copy = [...s];
      const j = idx + dir;
      if (j < 0 || j >= copy.length) return copy;
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy;
    });
    setDirty(true);
  };
  const removeStage = (idx) => {
    setDraftStages((s) => s.filter((_x, i) => i !== idx));
    setDirty(true);
  };
  const addStage = () => {
    setDraftStages((s) => [...s, { name: "" }]);
    setDirty(true);
  };

  const saveStages = async () => {
    if (!active) return;
    const cleaned = draftStages.map((s) => ({ stage_id: s.stage_id, name: s.name.trim() })).filter((s) => s.name);
    if (cleaned.length === 0) {
      toast.error("Add at least one stage");
      return;
    }
    setSavingStages(true);
    try {
      await api.put(`/pipelines/${active.pipeline_id}/stages`, { stages: cleaned });
      toast.success("Stages saved");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save stages");
    } finally {
      setSavingStages(false);
    }
  };

  const saveRename = async () => {
    if (!active) return;
    const n = renameName.trim();
    if (!n) { toast.error("Name is required"); return; }
    try {
      await api.patch(`/pipelines/${active.pipeline_id}`, {
        name: n, description: renameDesc,
      });
      toast.success("Pipeline updated");
      setRenaming(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update pipeline");
    }
  };

  const createPipeline = async (e) => {
    e.preventDefault();
    const name = newForm.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    const stages = newForm.stages
      .split(/,|\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((n) => ({ name: n }));
    if (stages.length === 0) stages.push({ name: "New" });
    setCreating(true);
    try {
      const { data } = await api.post("/pipelines", {
        name, description: newForm.description, stages,
      });
      toast.success("Pipeline created");
      setCreateOpen(false);
      setNewForm({ name: "", description: "", stages: "New" });
      await load();
      setActiveId(data.pipeline_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create pipeline");
    } finally {
      setCreating(false);
    }
  };

  const doDeletePipeline = async () => {
    if (!confirmDelete) return;
    const p = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/pipelines/${p.pipeline_id}`);
      toast.success("Pipeline deleted");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete pipeline");
    }
  };

  return (
    <div className="p-8 md:p-10 max-w-[1200px]" data-testid="pipelines-page">
      <div className="flex items-center gap-3 text-xs text-ink-secondary">
        <Link
          to="/leads"
          data-testid="pipelines-back"
          className="inline-flex items-center gap-1.5 hover:text-ink-primary"
        >
          <ArrowLeft weight="bold" className="h-3 w-3" />
          Leads
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-ink-primary">Pipelines</span>
      </div>

      <div className="mt-4 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">
            {currentOrg?.name || "Workspace"}
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Pipelines</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-lg">
            Define stages that match how your team actually sells. Every workspace has one Default Pipeline; add more for different campaigns.
          </p>
        </div>
        {isAdmin ? (
          <button
            data-testid="pipelines-create-button"
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors"
          >
            <Plus weight="bold" className="h-4 w-4" />
            New pipeline
          </button>
        ) : (
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/5 px-3 py-1.5 text-[11px] uppercase tracking-widest text-amber-300">
            <ShieldStar weight="fill" className="h-3 w-3" />
            Admins only
          </div>
        )}
      </div>

      {loading ? (
        <div className="mt-8 rounded-md border border-white/10 bg-graphite-900 p-10 text-center text-sm text-ink-secondary">
          Loading pipelines…
        </div>
      ) : pipelines.length === 0 ? (
        <div className="mt-8 rounded-md border border-white/10 bg-graphite-900 p-10 text-center text-sm text-ink-secondary">
          No pipelines yet.
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar of pipelines */}
          <aside className="lg:col-span-4 rounded-md border border-white/10 bg-graphite-900 overflow-hidden" data-testid="pipelines-sidebar">
            <div className="px-4 py-3 border-b border-white/10 text-[10px] uppercase tracking-[0.2em] text-ink-secondary">
              Your pipelines
            </div>
            <ul className="divide-y divide-white/5">
              {pipelines.map((p) => {
                const isActive = p.pipeline_id === activeId;
                return (
                  <li key={p.pipeline_id}>
                    <button
                      type="button"
                      data-testid={`pipelines-item-${p.pipeline_id}`}
                      onClick={() => setActiveId(p.pipeline_id)}
                      className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${
                        isActive ? "bg-white/[0.06]" : "hover:bg-white/[0.03]"
                      }`}
                    >
                      <div className={`mt-1 h-2 w-2 rounded-full ${isActive ? "bg-white" : "bg-white/20"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-sm text-ink-primary truncate">{p.name}</div>
                          {p.is_default && (
                            <span className="text-[9px] uppercase tracking-widest text-ink-secondary/70 border border-white/10 px-1 py-0.5 rounded-sm">Default</span>
                          )}
                        </div>
                        <div className="mt-0.5 text-[11px] text-ink-secondary">{p.stages.length} stages</div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Editor */}
          <section className="lg:col-span-8 space-y-6" data-testid="pipelines-editor">
            {active && (
              <>
                <div className="rounded-md border border-white/10 bg-graphite-900 p-6" data-testid="pipeline-header-card">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      {renaming ? (
                        <div className="space-y-3">
                          <Input
                            data-testid="pipeline-rename-name"
                            value={renameName}
                            onChange={(e) => setRenameName(e.target.value)}
                            className="bg-graphite-800 border-white/10 text-ink-primary text-xl"
                            placeholder="Pipeline name"
                          />
                          <Textarea
                            data-testid="pipeline-rename-desc"
                            value={renameDesc}
                            onChange={(e) => setRenameDesc(e.target.value)}
                            rows={2}
                            placeholder="Description"
                            className="bg-graphite-800 border-white/10 text-ink-primary text-sm"
                          />
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              data-testid="pipeline-rename-save"
                              onClick={saveRename}
                              className="rounded-md bg-white text-graphite-950 px-3 py-1.5 text-xs font-medium"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              data-testid="pipeline-rename-cancel"
                              onClick={() => { setRenaming(false); setRenameName(active.name); setRenameDesc(active.description || ""); }}
                              className="rounded-md border border-white/10 px-3 py-1.5 text-xs"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <h2 className="font-display text-2xl tracking-tight text-ink-primary" data-testid="pipeline-title">{active.name}</h2>
                            {active.is_default && (
                              <span className="text-[10px] uppercase tracking-widest text-ink-secondary/70 border border-white/10 px-1.5 py-0.5 rounded-sm">Default</span>
                            )}
                          </div>
                          {active.description && (
                            <p className="mt-2 text-sm text-ink-secondary">{active.description}</p>
                          )}
                        </>
                      )}
                    </div>
                    {isAdmin && !renaming && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          data-testid="pipeline-rename-open"
                          onClick={() => setRenaming(true)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary hover:bg-white/5"
                        >
                          <PencilSimple weight="bold" className="h-3 w-3" />
                          Rename
                        </button>
                        <button
                          type="button"
                          data-testid="pipeline-delete-open"
                          onClick={() => setConfirmDelete(active)}
                          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-secondary hover:text-accent-danger hover:bg-white/5"
                        >
                          <Trash weight="bold" className="h-3 w-3" />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="rounded-md border border-white/10 bg-graphite-900 p-6" data-testid="pipeline-stages-card">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary">Stages</div>
                      <div className="mt-1 text-sm text-ink-primary">Order defines the sequence in the pipeline</div>
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          data-testid="pipeline-stages-add"
                          onClick={addStage}
                          className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary hover:bg-white/5"
                        >
                          <Plus weight="bold" className="h-3 w-3" />
                          Add stage
                        </button>
                        <button
                          type="button"
                          data-testid="pipeline-stages-save"
                          onClick={saveStages}
                          disabled={!dirty || savingStages}
                          className="inline-flex items-center gap-1.5 rounded-full bg-white text-graphite-950 px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                        >
                          <FloppyDisk weight="bold" className="h-3 w-3" />
                          {savingStages ? "Saving…" : "Save order"}
                        </button>
                      </div>
                    )}
                  </div>

                  <ul className="mt-4 space-y-2" data-testid="pipeline-stages-list">
                    {draftStages.length === 0 && (
                      <li className="rounded-md border border-dashed border-white/10 p-6 text-center text-sm text-ink-secondary">
                        No stages yet. Add one to make this pipeline usable.
                      </li>
                    )}
                    {draftStages.map((s, i) => (
                      <li key={s.stage_id || `new-${i}`} data-testid={`pipeline-stage-row-${i}`} className="rounded-md border border-white/10 bg-graphite-800 p-3 flex items-center gap-3">
                        <div className="text-[10px] uppercase tracking-widest text-ink-secondary/70 w-8">#{i + 1}</div>
                        <Input
                          data-testid={`pipeline-stage-name-${i}`}
                          value={s.name}
                          onChange={(e) => updateStage(i, e.target.value)}
                          disabled={!isAdmin}
                          placeholder="Stage name"
                          className="flex-1 bg-graphite-900 border-white/10 text-ink-primary"
                        />
                        {isAdmin && (
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              data-testid={`pipeline-stage-up-${i}`}
                              onClick={() => moveStage(i, -1)}
                              disabled={i === 0}
                              className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-30"
                              title="Move up"
                            >
                              <ArrowUp weight="bold" className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              data-testid={`pipeline-stage-down-${i}`}
                              onClick={() => moveStage(i, 1)}
                              disabled={i === draftStages.length - 1}
                              className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5 disabled:opacity-30"
                              title="Move down"
                            >
                              <ArrowDown weight="bold" className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              data-testid={`pipeline-stage-remove-${i}`}
                              onClick={() => removeStage(i)}
                              className="p-2 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5"
                              title="Remove"
                            >
                              <Trash weight="bold" className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>

                  {!isAdmin && (
                    <p className="mt-4 text-[11px] text-ink-secondary/70 inline-flex items-center gap-1.5">
                      <Sparkle weight="duotone" className="h-3 w-3" />
                      Only admins can change pipeline stages.
                    </p>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      )}

      {/* Create pipeline dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="border-white/10 bg-graphite-800 text-ink-primary max-w-lg" data-testid="pipeline-create-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-light tracking-tight text-2xl">New pipeline</DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Give it a name and the initial stages. You can rename or reorder anytime.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createPipeline} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Name</label>
              <Input
                data-testid="pipeline-create-name"
                value={newForm.name}
                onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                placeholder="Q3 Enterprise campaign"
                className="bg-graphite-900 border-white/10 text-ink-primary"
                required
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Description</label>
              <Textarea
                data-testid="pipeline-create-desc"
                value={newForm.description}
                onChange={(e) => setNewForm({ ...newForm, description: e.target.value })}
                rows={2}
                className="bg-graphite-900 border-white/10 text-ink-primary"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">
                Initial stages (comma or newline separated)
              </label>
              <Textarea
                data-testid="pipeline-create-stages"
                value={newForm.stages}
                onChange={(e) => setNewForm({ ...newForm, stages: e.target.value })}
                rows={3}
                placeholder="New, Contacted, Follow Up, Won, Lost"
                className="bg-graphite-900 border-white/10 text-ink-primary font-mono text-xs"
              />
            </div>
            <DialogFooter>
              <button
                type="button"
                data-testid="pipeline-create-cancel"
                onClick={() => setCreateOpen(false)}
                className="rounded-md border border-white/10 px-4 py-2 text-sm hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="pipeline-create-submit"
                disabled={creating}
                className="rounded-md bg-white text-graphite-950 px-4 py-2 text-sm font-medium disabled:opacity-50"
              >
                {creating ? "Creating…" : "Create pipeline"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-graphite-800 text-ink-primary" data-testid="pipeline-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-light tracking-tight text-2xl">Delete pipeline?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary text-sm">
              &ldquo;{confirmDelete?.name}&rdquo; will be removed. Leads in this pipeline must be removed first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pipeline-delete-cancel" className="border-white/10 bg-transparent hover:bg-white/5 text-ink-primary">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="pipeline-delete-confirm" onClick={doDeletePipeline} className="bg-accent-danger text-graphite-950 hover:bg-red-300">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
